import React from "react";
import { Link } from "react-router-dom";
import { Building2, Check, ArrowRight, BarChart3, Users, TrendingUp, Calculator, Bell, Shield, PieChart, Landmark, Receipt } from "lucide-react";
import RevealOnScroll from "@/components/ui/RevelOnScroll";
import SleekButton from "@/components/ui/NavButtons";
import { SEO } from "@/components/common/SEO";

const APP_URL = "https://app.vawena.ai";

const FEATURES = [
  { icon: BarChart3, title: "Cap Rate & Cash-on-Cash Returns", desc: "Instantly see which properties are earning their keep. Stop manually calculating cap rate, cash-on-cash return, and GRM — PortfolioIQ does it automatically for every property you add." },
  { icon: Calculator, title: "DSCR Analysis & Lender Reports", desc: "Know your DSCR before your lender runs the numbers. Generate a professional lender-ready DSCR PDF in 60 seconds — exactly what DSCR loan underwriters need to move forward." },
  { icon: PieChart, title: "Portfolio-Level Analytics", desc: "See your entire portfolio at a glance — total equity, cash flow, overall DSCR, and ROI across every property. Instantly identify where your capital is working hardest." },
  { icon: Receipt, title: "Schedule E Tax Reports", desc: "Stop spending hours building tax summaries for your CPA. PortfolioIQ generates IRS Schedule E-ready reports by property — rental income, expenses, depreciation, ready to hand off." },
  { icon: Building2, title: "Unlimited Property Tracking", desc: "Add residential, multi-family, or commercial properties without limits. Track purchase price, current value, loan details, and key metrics in one organized dashboard." },
  { icon: Landmark, title: "Market Value Benchmarking", desc: "Compare your property's current value and rent against local comps. Know if you're above or below market without paying for an appraisal." },
  { icon: TrendingUp, title: "Rent & Cash Flow Tracking", desc: "Log rental income and expenses per property. Keep your cash flow analysis accurate and your Schedule E clean — without maintaining a spreadsheet." },
  { icon: Users, title: "Lease & Tenant Records", desc: "Store lease dates, tenant details, and rent status per unit. Your rent roll is always ready when a lender or accountant asks for it." },
  { icon: Bell, title: "DSCR & Performance Alerts", desc: "Get notified before problems become expensive. Automatic alerts when a property's DSCR drops below 1.0x or portfolio cash flow changes significantly." },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Add your properties",
    outcome: "Under 2 minutes per property",
    desc: "Enter each property — address, purchase price, loan details, rent, and expenses. PortfolioIQ does the rest. No formulas needed.",
  },
  {
    step: "02",
    title: "Get instant analytics",
    outcome: "Cap rate, DSCR, cash-on-cash — automatically",
    desc: "PortfolioIQ calculates every key metric across your portfolio the moment you save a property. See immediately which assets are performing and which aren't.",
  },
  {
    step: "03",
    title: "Generate reports on demand",
    outcome: "DSCR PDF in 60 seconds. Schedule E done.",
    desc: "One click generates lender-ready DSCR reports and CPA-ready Schedule E summaries. Share directly with your mortgage broker or accountant.",
  },
];

const PLAN_FEATURES = [
  "Unlimited property tracking",
  "Cap rate & cash-on-cash returns",
  "DSCR analysis & lender PDF reports",
  "Schedule E tax reports for your CPA",
  "Property performance dashboards",
  "Portfolio-level analytics",
  "Market value benchmarking",
  "Rent & income tracking",
  "Lease & tenant records",
  "Performance alerts",
];

const schema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "PortfolioIQ by VaWeNa",
  "applicationCategory": "FinanceApplication",
  "description": "Investment portfolio analytics for real estate investors. Track cap rates, DSCR, cash-on-cash returns, generate Schedule E tax reports and lender-ready DSCR reports.",
  "offers": { "@type": "Offer", "price": "29.99", "priceCurrency": "USD" },
  "url": "https://vawena.ai/portfolioiq",
};

const PortfolioIQPage: React.FC = () => {
  return (
    <>
      <SEO
        title="PortfolioIQ — Real Estate Portfolio Analytics, DSCR & Schedule E Reports"
        description="PortfolioIQ tracks cap rate, DSCR, and cash-on-cash returns across your entire real estate portfolio. Generate Schedule E tax reports for your CPA and lender-ready DSCR reports in one click. 14-day free trial."
        canonical="/portfolioiq"
        schema={schema}
      />

      <div className="min-h-screen bg-vw-bg">
        {/* ── Hero ─────────────────────────────────────────────── */}
        <section className="relative pt-20 pb-24 px-4 sm:px-6 overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[450px] bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-[1000px] mx-auto text-center relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-semibold mb-6">
              <Building2 className="w-4 h-4" />
              PortfolioIQ by VaWeNa
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-5">
              Know Exactly Which Properties{" "}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Are Making You Money</span>
            </h1>

            <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-4 leading-relaxed">
              PortfolioIQ automatically calculates cap rate, DSCR, and cash-on-cash returns across every property — then generates lender-ready reports and Schedule E summaries your CPA can actually use.
            </p>

            {/* Audience callout */}
            <p className="text-sm text-blue-400 font-semibold mb-8">
              Built for investors managing 2 or more rental properties.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <SleekButton variant="primary" onClick={() => window.open(`${APP_URL}/signup`, "_self")}>
                Analyze My Portfolio — Free
              </SleekButton>
              <Link to="/pricing">
                <SleekButton variant="light">View Pricing →</SleekButton>
              </Link>
            </div>
            <p className="mt-4 text-sm text-slate-500">Credit card required · We'll remind you 3 days before you're charged · Cancel anytime</p>
          </div>
        </section>

        {/* ── Stats ─────────────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-12 px-4 border-y border-white/5">
            <div className="max-w-[1000px] mx-auto grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
              {[
                { value: "Unlimited", label: "properties tracked" },
                { value: "60 sec", label: "DSCR lender report" },
                { value: "Schedule E", label: "ready for your CPA" },
                { value: "17%", label: "savings on annual plan" },
              ].map(({ value, label }) => (
                <div key={label}>
                  <p className="text-2xl sm:text-3xl font-extrabold text-white mb-1">{value}</p>
                  <p className="text-sm text-slate-500">{label}</p>
                </div>
              ))}
            </div>
          </section>
        </RevealOnScroll>

        {/* ── Schedule E callout ───────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-10 px-4 sm:px-6">
            <div className="max-w-[900px] mx-auto bg-blue-500/5 border border-blue-500/20 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="w-12 h-12 rounded-xl bg-blue-500/15 flex items-center justify-center shrink-0">
                <Receipt className="w-6 h-6 text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-bold text-lg mb-1">Stop building tax spreadsheets for your CPA every year</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  PortfolioIQ generates IRS Schedule E-ready reports — rental income, expenses, and depreciation broken down by property, structured exactly how your accountant needs them. Export in one click, hand it off.
                </p>
              </div>
              <button
                type="button"
                onClick={() => window.open(`${APP_URL}/signup`, "_self")}
                className="shrink-0 inline-flex items-center gap-2 px-5 h-10 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition-colors whitespace-nowrap"
              >
                Try free <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </section>
        </RevealOnScroll>

        {/* ── Features ─────────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-16 px-4 sm:px-6">
            <div className="max-w-[1000px] mx-auto">
              <div className="text-center mb-12">
                <p className="text-xs font-bold uppercase tracking-widest text-blue-500 mb-3">What you get</p>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
                  Retire the spreadsheets for good
                </h2>
                <p className="text-slate-400 max-w-xl mx-auto">
                  Every metric, report, and alert serious real estate investors need — automatically calculated and ready to act on.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {FEATURES.map(({ icon: Icon, title, desc }) => (
                  <div
                    key={title}
                    className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 hover:border-blue-500/30 hover:bg-blue-500/5 transition-all duration-300"
                  >
                    <div className="w-9 h-9 rounded-xl bg-blue-500/10 flex items-center justify-center mb-4">
                      <Icon className="w-4 h-4 text-blue-400" />
                    </div>
                    <h3 className="text-white font-semibold mb-2 text-sm">{title}</h3>
                    <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </RevealOnScroll>

        {/* ── How it works ─────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-20 px-4 sm:px-6 bg-white/[0.01]">
            <div className="max-w-[900px] mx-auto text-center">
              <p className="text-xs font-bold uppercase tracking-widest text-blue-500 mb-3">How it works</p>
              <h2 className="text-3xl font-extrabold text-white mb-14">
                From your first property to a full portfolio
              </h2>
              <div className="grid sm:grid-cols-3 gap-10">
                {HOW_IT_WORKS.map(({ step, title, outcome, desc }) => (
                  <div key={step}>
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 text-lg font-extrabold mb-5">
                      {step}
                    </div>
                    <h3 className="text-white font-bold mb-1">{title}</h3>
                    <p className="text-xs text-blue-400 font-semibold mb-2">{outcome}</p>
                    <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </RevealOnScroll>

        {/* ── DSCR callout ─────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-16 px-4 sm:px-6">
            <div className="max-w-[900px] mx-auto bg-white/[0.02] border border-white/10 rounded-3xl p-8 sm:p-10">
              <div className="grid sm:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-blue-500 mb-3">Lender reports</p>
                  <h3 className="text-2xl font-extrabold text-white mb-4">
                    DSCR reports your lender will accept — in 60 seconds
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-6">
                    Stop emailing your mortgage broker disorganized spreadsheets. Generate a professional DSCR report with net operating income, debt service, property-level and portfolio-level DSCR — exactly what underwriters require to move forward.
                  </p>
                  <button
                    type="button"
                    onClick={() => window.open(`${APP_URL}/signup`, "_self")}
                    className="inline-flex items-center gap-2 px-6 h-10 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition-colors"
                  >
                    Generate my first report <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "DSCR Lender Report", sub: "PDF · For mortgage underwriters" },
                    { label: "Schedule E Report", sub: "PDF · For your CPA" },
                    { label: "Portfolio Summary", sub: "PDF · Full analytics view" },
                    { label: "Rent Roll Export", sub: "PDF · Per property" },
                  ].map(({ label, sub }) => (
                    <div key={label} className="bg-blue-500/5 border border-blue-500/15 rounded-xl p-4">
                      <p className="text-sm text-white font-semibold mb-1">{label}</p>
                      <p className="text-[11px] text-slate-500">{sub}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </RevealOnScroll>

        {/* ── Pricing CTA ──────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-20 px-4 sm:px-6">
            <div className="max-w-[560px] mx-auto bg-white/[0.03] border border-white/10 rounded-3xl p-8 sm:p-10 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-semibold mb-6">
                <Check className="w-3.5 h-3.5" />
                14-day free trial · We'll remind you before you're charged
              </div>
              <h2 className="text-3xl font-extrabold text-white mb-2">PortfolioIQ</h2>
              <div className="flex items-end justify-center gap-1 mb-1">
                <span className="text-5xl font-extrabold text-white">$29.99</span>
                <span className="text-slate-500 mb-1.5">/month</span>
              </div>
              <p className="text-sm text-blue-400 font-semibold mb-7">or $299.90/year — save 17% billed annually</p>
              <ul className="text-left space-y-2.5 mb-8 max-w-xs mx-auto">
                {PLAN_FEATURES.map((f) => (
                  <li key={f} className="flex items-center gap-2.5 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-blue-400 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => window.open(`${APP_URL}/signup`, "_self")}
                className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm transition-colors"
              >
                Start Free 14-Day Trial
              </button>
              <p className="mt-4 text-xs text-slate-500">We'll email you 3 days before your trial ends. Cancel anytime.</p>
            </div>
          </section>
        </RevealOnScroll>

        {/* ── Trust ────────────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-12 px-4 border-t border-white/5">
            <div className="max-w-[900px] mx-auto">
              <p className="text-center text-xs font-bold uppercase tracking-widest text-slate-400 mb-8">Enterprise-grade security for your property data</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
                {[
                  { icon: Shield, label: "Row-level security (RLS)" },
                  { icon: Shield, label: "AES-256 data encryption" },
                  { icon: Check, label: "HTTPS/TLS in transit" },
                  { icon: Shield, label: "Google Cloud Armor WAF" },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex flex-col items-center gap-2">
                    <Icon className="w-4 h-4 text-slate-400" />
                    <p className="text-xs text-slate-400">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </RevealOnScroll>

        {/* ── Bottom CTA ───────────────────────────────────────── */}
        <RevealOnScroll direction="up">
          <section className="py-20 px-4 text-center">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
              Your portfolio deserves better than a spreadsheet.
            </h2>
            <p className="text-slate-500 mb-2 max-w-md mx-auto">
              Get the analytics, lender reports, and Schedule E summaries that serious real estate investors need to make decisions with confidence.
            </p>
            <p className="text-sm text-blue-400 font-semibold mb-8">Free for 14 days. We'll remind you before you're charged.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                type="button"
                onClick={() => window.open(`${APP_URL}/signup`, "_self")}
                className="inline-flex items-center gap-2 px-8 h-12 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition-colors"
              >
                Analyze My Portfolio — Free <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/wealthscope">
                <button type="button" className="inline-flex items-center gap-2 px-8 h-12 rounded-full border border-white/10 text-slate-300 font-semibold text-sm hover:bg-white/5 transition-colors">
                  Pre-investment? See WealthScope →
                </button>
              </Link>
            </div>
          </section>
        </RevealOnScroll>
      </div>
    </>
  );
};

export default PortfolioIQPage;
